// src/pages/expenses/ExpenseList.js
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import API_BASE_URL from "../../utils/config";

export default function ExpenseList() {
  const [expenses, setExpenses] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [showModal, setShowModal] = useState(false);

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const [form, setForm] = useState({
    expense_type: "",
    description: "",
    amount: "",
    purchase_id: null, // react-select object
    invoice_id: null, // react-select object
    expense_date: "",
  });

  const navigate = useNavigate();

  // Helpers
  const fmtINR = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  const getCurrentMonthRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const toStr = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    return { start: toStr(first), end: toStr(last) };
  };

  // Init: default date range + lookups
  useEffect(() => {
    const { start, end } = getCurrentMonthRange();
    setStartDate(start);
    setEndDate(end);
    fetchLookups();
  }, []);

  const fetchLookups = async () => {
    const token = localStorage.getItem("token");
    try {
      const [poRes, invRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/purchases`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/api/invoices`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setPurchaseOrders(
        (poRes.data || []).map((po) => ({
          value: po.id,
          label: `PO-${po.id} (${new Date(po.created_at).toLocaleDateString(
            "en-GB"
          )})`,
        }))
      );

      setInvoices(
        (invRes.data || []).map((inv) => ({
          value: inv.id,
          label: `INV-${inv.id} (${new Date(inv.created_at).toLocaleDateString(
            "en-GB"
          )})`,
        }))
      );
    } catch (e) {
      console.error("Lookup fetch failed:", e);
    }
  };

  // Fetch filtered expenses
  const fetchExpenses = async () => {
    const token = localStorage.getItem("token");
    try {
      setLoading(true);
      setErr("");
      const res = await axios.get(`${API_BASE_URL}/api/expenses`, {
        params: { startDate, endDate },
        headers: { Authorization: `Bearer ${token}` },
      });

      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setExpenses(list);
    } catch (e) {
      console.error("Failed to fetch expenses:", e);
      setErr("Failed to load expenses.");
    } finally {
      setLoading(false);
    }
  };

  // Load on date change
  useEffect(() => {
    if (startDate && endDate) fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Metrics
  const totalAmount = useMemo(
    () => expenses.reduce((s, e) => s + Number(e.amount || 0), 0),
    [expenses]
  );

  // Add expense submit
  const handleAddExpense = async () => {
    const token = localStorage.getItem("token");
    if (!form.expense_type || !form.amount || !form.expense_date) {
      alert("Expense Type, Amount and Date are required.");
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/expenses`,
        {
          ...form,
          purchase_id: form.purchase_id?.value || null,
          invoice_id: form.invoice_id?.value || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowModal(false);
      // Reset minimal fields for next entry
      setForm((f) => ({
        ...f,
        expense_type: "",
        description: "",
        amount: "",
        purchase_id: null,
        invoice_id: null,
        expense_date: "",
      }));
      fetchExpenses();
    } catch (err) {
      console.error("Add expense failed:", err);
      alert("Failed to save expense.");
    }
  };

  const typeChip = (t) => {
    if (!t) return <span className="text-slate-400">—</span>;
    const tone =
      t.toLowerCase().includes("transport") || t.toLowerCase().includes("fuel")
        ? "bg-amber-50 text-amber-700"
        : t.toLowerCase().includes("salary")
        ? "bg-emerald-50 text-emerald-700"
        : "bg-slate-100 text-slate-700";
    return (
      <span className={`rounded-full px-2 py-0.5 text-xs ${tone}`}>{t}</span>
    );
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header + filters */}
      <div className="mx-auto mb-4 flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Expense List
          </h1>
          <div className="mt-1 text-sm text-slate-500">
            Showing{" "}
            <span className="font-medium text-slate-700">
              {new Date(startDate).toLocaleDateString("en-GB")} –{" "}
              {new Date(endDate).toLocaleDateString("en-GB")}
            </span>
          </div>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:w-44"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:w-44"
          />
          <button
            onClick={fetchExpenses}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
          >
            Filter
          </button>

          <button
            onClick={() => setShowModal(true)}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700"
          >
            + Add Expense
          </button>

          <Link
            to="/expenses/profit"
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
          >
            📊 Show Profit
          </Link>
        </div>
      </div>

      {/* Summary chips */}
      <div className="mx-auto mb-4 grid max-w-7xl grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
          <div className="text-xs text-slate-500">Entries</div>
          <div className="text-lg font-semibold">{expenses.length}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
          <div className="text-xs text-slate-500">Total Amount</div>
          <div className="text-lg font-semibold tabular-nums">
            {fmtINR(totalAmount)}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
          <div className="text-xs text-slate-500">From</div>
          <div className="text-sm font-medium">
            {new Date(startDate).toLocaleDateString("en-GB")}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
          <div className="text-xs text-slate-500">To</div>
          <div className="text-sm font-medium">
            {new Date(endDate).toLocaleDateString("en-GB")}
          </div>
        </div>
      </div>

      {/* Error / Loading */}
      {err && (
        <div className="mx-auto mb-3 max-w-7xl rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {err}
        </div>
      )}
      {loading && (
        <div className="mx-auto max-w-7xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          Loading…
        </div>
      )}

      {/* Table (desktop) */}
      {!loading && (
        <div className="mx-auto max-w-7xl overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="hidden sm:block">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Description</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">
                    Purchase Order
                  </th>
                  <th className="px-3 py-2 text-left font-medium">Invoice</th>
                  <th className="px-3 py-2 text-left font-medium">Added By</th>
                </tr>
              </thead>
              <tbody className="[&>tr:nth-child(even)]:bg-slate-50">
                {expenses.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      {e.expense_date
                        ? new Date(e.expense_date).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className="px-3 py-2">{e.description || "-"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtINR(e.amount)}
                    </td>
                    <td className="px-3 py-2">{typeChip(e.expense_type)}</td>
                    <td className="px-3 py-2">
                      {e.purchase_id ? (
                        <Link
                          to={`/purchases/${e.purchase_id}`}
                          className="text-blue-700 hover:underline"
                        >
                          PO-{e.purchase_id}
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {e.invoice_id ? (
                        <Link
                          to={`/invoices/${e.invoice_id}`}
                          className="text-blue-700 hover:underline"
                        >
                          INV-{e.invoice_id}
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {e.created_by_name || e.created_by || "-"}
                    </td>
                  </tr>
                ))}

                {expenses.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-6 text-center text-slate-500"
                    >
                      No expenses in this range.{" "}
                      <button
                        onClick={() => setShowModal(true)}
                        className="text-blue-700 underline"
                      >
                        Add one
                      </button>
                      .
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="divide-y divide-slate-100 sm:hidden">
            {expenses.length === 0 && (
              <div className="p-4 text-center text-sm text-slate-500">
                No expenses in this range.
              </div>
            )}
            {expenses.map((e) => (
              <div key={e.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="font-medium text-slate-900">
                    {e.description || "—"}
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Amount</div>
                    <div className="tabular-nums font-semibold">
                      {fmtINR(e.amount)}
                    </div>
                  </div>
                </div>

                <div className="mt-1 text-sm">
                  <div className="text-slate-500">Date</div>
                  <div>
                    {e.expense_date
                      ? new Date(e.expense_date).toLocaleDateString("en-GB")
                      : "-"}
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {typeChip(e.expense_type)}
                  {e.purchase_id && (
                    <Link
                      to={`/purchases/${e.purchase_id}`}
                      className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-blue-700"
                    >
                      PO-{e.purchase_id}
                    </Link>
                  )}
                  {e.invoice_id && (
                    <Link
                      to={`/invoices/${e.invoice_id}`}
                      className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-blue-700"
                    >
                      INV-{e.invoice_id}
                    </Link>
                  )}
                </div>

                <div className="mt-1 text-xs text-slate-500">
                  Added by:{" "}
                  <span className="text-slate-700">
                    {e.created_by_name || e.created_by || "-"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <h3 className="text-lg font-semibold">Add Expense</h3>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 px-4 py-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <label className="mb-1 block text-xs text-slate-600">
                    Expense Type *
                  </label>
                  <input
                    type="text"
                    value={form.expense_type}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, expense_type: e.target.value }))
                    }
                    placeholder="e.g., Transport, Salary, Electricity"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div className="sm:col-span-1">
                  <label className="mb-1 block text-xs text-slate-600">
                    Amount *
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                      ₹
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.amount}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, amount: e.target.value }))
                      }
                      placeholder="0.00"
                      className="w-full rounded-lg border border-slate-300 px-7 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs text-slate-600">
                    Description
                  </label>
                  <textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, description: e.target.value }))
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Optional details…"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs text-slate-600">
                    Link Purchase Order
                  </label>
                  <Select
                    className="text-sm"
                    options={purchaseOrders}
                    value={form.purchase_id}
                    onChange={(val) => setForm((f) => ({ ...f, purchase_id: val }))}
                    isClearable
                    placeholder="Select Purchase Order…"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs text-slate-600">
                    Link Invoice
                  </label>
                  <Select
                    className="text-sm"
                    options={invoices}
                    value={form.invoice_id}
                    onChange={(val) => setForm((f) => ({ ...f, invoice_id: val }))}
                    isClearable
                    placeholder="Select Invoice…"
                  />
                </div>

                <div className="sm:col-span-1">
                  <label className="mb-1 block text-xs text-slate-600">
                    Expense Date *
                  </label>
                  <input
                    type="date"
                    value={form.expense_date}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, expense_date: e.target.value }))
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddExpense}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
